import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useBulkCreateProductSKU, useGroupAnalyzeProductSKU } from "@/hooks/useProductSKU";
import { uploadFileAndReturnUrl } from "@/services/api/gcs.service";
import {
  EditableProduct,
  MAX_SMART_UPLOAD,
  SmartUploadFormValues,
  UploadItem,
} from "@/schema/smartupload.schema";
import {
  buildBulkPayload,
  mapAnalysisToFormValues,
} from "@/lib/smartupload.utils";
import { AnalyzingView } from "./SmartUpload/AnalyzingView";
import { ResultsView } from "./SmartUpload/ResultsView";
import { UploadView } from "./SmartUpload/UploadView";
import { useSmartUploadStore } from "@/store/smartupload.store";

type View = "upload" | "analyzing" | "results";


export function SmartUploadDialog({
  open,
  onOpenChange,
  brandId,
  onSaved,
  initialFiles,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandId: string;
  onSaved?: () => void;
  initialFiles?: File[];
}) {
  const [items, setItems] = useState<UploadItem[]>([]);

  useEffect(() => {
    if (open && initialFiles && initialFiles.length > 0) {
      const newItems = initialFiles
        .filter((f) => f.type.startsWith("image/"))
        .slice(0, MAX_SMART_UPLOAD)
        .map((f) => ({ file: f, previewUrl: URL.createObjectURL(f) }));
      setItems(newItems);
    }
  }, [open]);
  const [isUploading, setIsUploading] = useState(false);
  const [view, setView] = useState<View>("upload");
  const [initialProducts, setInitialProducts] = useState<EditableProduct[]>([]);

  const bulkCreateMutation = useBulkCreateProductSKU(brandId);
  const groupAnalyzeMutation = useGroupAnalyzeProductSKU(brandId);
  const clearRemovedImages = useSmartUploadStore((s) => s.clearRemovedImages);

  const handleClose = () => {
    items.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl));
    setItems([]);
    setView("upload");
    setInitialProducts([]);
    clearRemovedImages();
    onOpenChange(false);
  };

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const remaining = MAX_SMART_UPLOAD - items.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_SMART_UPLOAD} images allowed.`);
      return;
    }
    const all = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (all.length > remaining) {
      toast.warning(
        `Only ${remaining} more image${remaining !== 1 ? "s" : ""} can be added. Maximum is ${MAX_SMART_UPLOAD}.`,
      );
    }
    const newItems = all.slice(0, remaining).map((f) => ({
      file: f,
      previewUrl: URL.createObjectURL(f),
    }));
    setItems((prev) => [...prev, ...newItems]);
  };

  const handleRemoveItem = (previewUrl: string) => {
    URL.revokeObjectURL(previewUrl);
    setItems((prev) => prev.filter((item) => item.previewUrl !== previewUrl));
  };

  const handleClearAll = () => {
    items.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl));
    setItems([]);
  };

  const handleStartAISorting = async () => {
    setIsUploading(true);
    let uploadedUrls: string[];
    try {
      uploadedUrls = await Promise.all(
        items.map(({ file }) =>
          uploadFileAndReturnUrl(file.name, file.type, "product-sku", file, brandId),
        ),
      );
    } catch {
      toast.error("Upload failed. Please try again.");
      setIsUploading(false);
      return;
    }
    setIsUploading(false);
    setView("analyzing");

    try {
      const result = await groupAnalyzeMutation.mutateAsync({ image_urls: uploadedUrls });
      setInitialProducts(mapAnalysisToFormValues(result).products);
      setView("results");
    } catch {
      toast.error("Analysis failed. Please try again.");
      setView("upload");
    }
  };

  const handleSaveProducts = async (values: SmartUploadFormValues) => {
    const count = values.products.length;
    handleClose();
    onSaved?.();
    const toastId = toast.loading("Analysing products...");
    try {
      await bulkCreateMutation.mutateAsync(buildBulkPayload(values));
      toast.success(`${count} product${count !== 1 ? "s" : ""} saved.`, { id: toastId });
    } catch {
      toast.error("Failed to save products. Please try again.", { id: toastId });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      {/* <DialogContent className="p-0 h-[100dvh] w-[100dvw] max-w-[100dvw]! min-w-full rounded-none shadow-xl overflow-hidden flex flex-col"> */}
      <DialogContent className="sm:max-w-none !max-w-[95vw] w-[1000px] h-[70vh] flex flex-col p-0 gap-0 overflow-hidden">
        {view === "analyzing" && <AnalyzingView />}
        {view === "results" && (
          <ResultsView
            initialProducts={initialProducts}
            isPending={bulkCreateMutation.isPending}
            onSave={handleSaveProducts}
            onBack={() => {
              setView("upload");
              setInitialProducts([]);
              clearRemovedImages();
            }}
          />
        )}
        {view === "upload" && (
          <UploadView
            items={items}
            isUploading={isUploading}
            onAddFiles={addFiles}
            onRemoveItem={handleRemoveItem}
            onClearAll={handleClearAll}
            onStartAISorting={handleStartAISorting}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
